interface VCardData {
  name: string
  phoneNumber: string
  photoUrl?: string | null | undefined
  // The assistant's voice line, added as a second TEL labelled "Call" so the
  // saved contact is always dialable. Skipped when it matches phoneNumber.
  voicePhoneNumber?: string | null | undefined
}

function phoneDigits(number: string): string {
  return number.replace(/\D/g, '')
}

async function fetchImageAsBase64(url: string): Promise<{ base64: string, mediaType: string } | null> {
  try {
    const response = await fetch(url)
    if (!response.ok) return null

    const blob = await response.blob()
    const mediaType = blob.type || 'image/jpeg'
    const buffer = await blob.arrayBuffer()
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    )
    return { base64, mediaType }
  } catch {
    return null
  }
}

function mediaTypeToVCardType(mediaType: string): string {
  const mapping: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
  }
  return mapping[mediaType] ?? 'JPEG'
}

async function generateVCard({ name, phoneNumber, photoUrl, voicePhoneNumber }: VCardData): Promise<string> {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${name}`,
    `TEL;TYPE=CELL:${phoneNumber}`,
  ]

  if (voicePhoneNumber && phoneDigits(voicePhoneNumber) !== phoneDigits(phoneNumber)) {
    lines.push(`item1.TEL;TYPE=VOICE:${voicePhoneNumber}`)
    lines.push('item1.X-ABLabel:Call')
  }

  if (photoUrl) {
    const image = await fetchImageAsBase64(photoUrl)
    if (image) {
      const type = mediaTypeToVCardType(image.mediaType)
      lines.push(`PHOTO;ENCODING=b;TYPE=${type}:${image.base64}`)
    }
  }

  lines.push('END:VCARD')
  return lines.join('\r\n')
}

// Hand a Blob to the browser as a file download. Shared so the object-URL
// dance - including the revoke, which leaks the blob if forgotten - is written
// once rather than per caller.
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function downloadVCard(data: VCardData): Promise<void> {
  const vcardString = await generateVCard(data)
  const blob = new Blob([vcardString], { type: 'text/vcard;charset=utf-8' })
  downloadBlob(blob, `${data.name}.vcf`)
}
